import { SubscriptionCustomer } from "@/types/subscription";

/**
 * Creates a Stripe customer
 * @param organizationId The organization ID
 * @param customerPayload The customer payload to create
 */
export async function createCustomer(
  organizationId: string,
  customerPayload: SubscriptionCustomer,
) {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), 30000);

  try {
    const response = await fetch("/api/stripe/customers", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        organizationId,
        customerPayload,
      }),
      signal: controller.signal,
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || "Failed to create Stripe customer");
    }

    try {
      const { customerId } = await response.json();
      return customerId;
    } catch (error: any) {
      throw new Error(error.message || "Failed to create Stripe customer");
    }
  } finally {
    clearTimeout(timeoutId);
  }
}

/**
 * Gets the customer from the checkout session ID
 * @param sessionId The checkout session ID from the payment link
 */
export async function getCustomerFromSessionID(sessionId: string) {
  const response = await fetch(
    `/api/stripe/sessions?sessionId=${encodeURIComponent(sessionId)}`,
    {
      method: "GET",
    },
  );

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(
      errorData.error || "Failed to fetch customer from session ID",
    );
  }

  const { customer } = await response.json();
  return customer;
}

/**
 * Searches for a customer by ownerId
 * @param organizationId The organization ID
 * @param ownerId The owner ID of the organization
 */
export async function searchCustomerByOwnerEmail(
  organizationId: string,
  ownerId: string,
) {
  const response = await fetch(
    `/api/stripe/customers?orgId=${encodeURIComponent(organizationId)}&ownerId=${encodeURIComponent(ownerId)}`,
    {
      method: "GET",
    },
  );

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.error || "Failed to search customer by ownerId");
  }

  const { data } = await response.json();
  return data;
}
